import React, { useState, useCallback } from 'react';
import { View, StyleSheet, FlatList, RefreshControl, Alert, TouchableOpacity } from 'react-native';
import {
  Text,
  Card,
  ActivityIndicator,
  Portal,
  Dialog,
  TextInput,
  Button,
  HelperText,
  IconButton,
} from 'react-native-paper';
import { useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import apiService from '../../services/api';

const PresentacionesScreen = () => {
  const insets = useSafeAreaInsets();
  const [presentaciones, setPresentaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [formVisible, setFormVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [nombre, setNombre] = useState('');
  const [abreviatura, setAbreviatura] = useState('');
  const [errorNombre, setErrorNombre] = useState('');
  const [errorAbrev, setErrorAbrev] = useState('');
  const [saving, setSaving] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const data = await apiService.getPresentaciones();
      setPresentaciones(Array.isArray(data) ? data : []);
    } catch (error) {
      Alert.alert('Error', 'No se pudieron cargar las presentaciones');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { cargar(); }, [cargar]));

  const abrirCrear = () => {
    setEditing(null);
    setNombre('');
    setAbreviatura('');
    setErrorNombre('');
    setErrorAbrev('');
    setFormVisible(true);
  };

  const abrirEditar = (item) => {
    setEditing(item);
    setNombre(item.nombre || '');
    setAbreviatura(item.abreviatura || '');
    setErrorNombre('');
    setErrorAbrev('');
    setFormVisible(true);
  };

  const cerrarForm = () => {
    if (saving) return;
    setFormVisible(false);
  };

  const guardar = async () => {
    const n = nombre.trim();
    const a = abreviatura.trim();
    let bad = false;
    if (!n) { setErrorNombre('Obligatorio'); bad = true; }
    if (!a) { setErrorAbrev('Obligatoria'); bad = true; }
    if (bad) return;

    setSaving(true);
    try {
      const payload = { nombre: n, abreviatura: a };
      if (editing) {
        await apiService.updatePresentacion(editing.id, payload);
      } else {
        await apiService.createPresentacion(payload);
      }
      setFormVisible(false);
      await cargar();
    } catch (error) {
      Alert.alert('Error', error.message || 'No se pudo guardar la presentación');
    } finally {
      setSaving(false);
    }
  };

  const confirmarEliminar = (item) => {
    Alert.alert(
      'Eliminar presentación',
      `¿Eliminar "${item.nombre}"? Si está asignada a algún producto, no se podrá borrar.`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Eliminar',
          style: 'destructive',
          onPress: async () => {
            try {
              await apiService.deletePresentacion(item.id);
              await cargar();
            } catch (error) {
              Alert.alert('No se pudo eliminar', error.message || 'Verifica que ningún producto la use.');
            }
          },
        },
      ]
    );
  };

  const renderItem = ({ item }) => (
    <Card style={styles.card} onPress={() => abrirEditar(item)}>
      <Card.Content style={styles.cardContent}>
        <View style={{ flex: 1 }}>
          <Text style={styles.nombre}>{item.nombre}</Text>
          <Text style={styles.abrev}>Abrev: {item.abreviatura}</Text>
        </View>
        <IconButton
          icon={({ size, color }) => <Ionicons name="trash-outline" size={size} color={color} />}
          iconColor="#DC3545"
          onPress={() => confirmarEliminar(item)}
        />
      </Card.Content>
    </Card>
  );

  if (loading) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator size="large" color="#0066cc" />
        <Text style={{ marginTop: 12 }}>Cargando presentaciones...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.hintBar}>
        <Ionicons name="information-circle-outline" size={16} color="#0066cc" />
        <Text style={styles.hintText}>
          Estas son las presentaciones globales. Asigna el factor por producto al editarlo.
        </Text>
      </View>

      <FlatList
        data={presentaciones}
        renderItem={renderItem}
        keyExtractor={(i) => i.id.toString()}
        contentContainerStyle={styles.list}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => { setRefreshing(true); cargar(); }}
          />
        }
        ListEmptyComponent={
          <View style={styles.empty}>
            <Ionicons name="cube-outline" size={48} color="#adb5bd" />
            <Text style={styles.emptyText}>Aún no hay presentaciones</Text>
            <Text style={styles.emptyHint}>Toca el botón + para crear la primera</Text>
          </View>
        }
      />

      <TouchableOpacity style={[styles.fab, { bottom: 20 + insets.bottom }]} onPress={abrirCrear}>
        <Ionicons name="add" size={26} color="white" />
      </TouchableOpacity>

      <Portal>
        <Dialog visible={formVisible} onDismiss={cerrarForm}>
          <Dialog.Title>{editing ? 'Editar presentación' : 'Nueva presentación'}</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label="Nombre (ej: Caja, Docena)"
              value={nombre}
              onChangeText={(t) => { setNombre(t); if (errorNombre) setErrorNombre(''); }}
              mode="outlined"
              autoFocus
              error={!!errorNombre}
            />
            {!!errorNombre && <HelperText type="error">{errorNombre}</HelperText>}
            <TextInput
              label="Abreviatura (ej: caja, doc)"
              value={abreviatura}
              onChangeText={(t) => { setAbreviatura(t); if (errorAbrev) setErrorAbrev(''); }}
              mode="outlined"
              style={{ marginTop: 12 }}
              error={!!errorAbrev}
            />
            {!!errorAbrev && <HelperText type="error">{errorAbrev}</HelperText>}
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={cerrarForm} disabled={saving}>Cancelar</Button>
            <Button onPress={guardar} loading={saving} disabled={saving} mode="contained">
              {editing ? 'Guardar' : 'Crear'}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8f9fa' },
  loader: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  hintBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E7F1FF',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 6,
  },
  hintText: { flex: 1, fontSize: 12, color: '#0066cc' },
  list: { padding: 12, paddingBottom: 96 },
  card: { marginBottom: 8, backgroundColor: 'white' },
  cardContent: { flexDirection: 'row', alignItems: 'center', paddingVertical: 4 },
  nombre: { fontSize: 16, fontWeight: '600', color: '#212529' },
  abrev: { fontSize: 13, color: '#6c757d', marginTop: 2 },
  empty: { alignItems: 'center', paddingVertical: 64 },
  emptyText: { fontSize: 16, color: '#6c757d', marginTop: 12 },
  emptyHint: { fontSize: 13, color: '#adb5bd', marginTop: 4 },
  fab: {
    position: 'absolute',
    right: 20,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#0066cc',
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 4,
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 4,
  },
});

export default PresentacionesScreen;
